// src/components/PeoplePlan/EmployeeManagement/GenericEmployeeTable.js
import React, { useState, useEffect } from "react";
import apiClient from "../../../apiClient";
import WorkflowTransition from "../WorkflowTransition";
import WorkflowPanel from "./WorkflowPanel";
import EmployeeTableGrid from "./EmployeeTableGrid";
import ConfirmationDialog from "./Dialogs/ConfirmationDialog";
import NotificationBanner from "./Dialogs/NotificationBanner";

// Hooks
import { useEmployeeData } from "./hooks/useEmployeeData";
import { useWorkflowManagement } from "./hooks/useWorkflowManagement";
import { useEmployeeSelection } from "./hooks/useEmployeeSelection";
import { useEmptyPositionSelection } from "./hooks/useEmptyPositionSelection";
import { useNotifications } from "./hooks/useNotification";

// Styles
import styles from "./GenericEmployeeTable.module.css";

// Icons
import exitSvg from "../assets/exit.svg";
import developingSvg from "../assets/developing.svg";
import promoteSvg from "../assets/promote.svg";
import transferSvg from "../assets/transfer.svg";
import skillPlanSvg from "../assets/skillplan.svg";
import requestSvg from "../assets/request.svg";
import trainingSvg from "../assets/training.svg";

const GenericEmployeeTable = ({
  selectedMUM,
  storeData,
  roleConfig,
  onNavigateToProfile,
  customWorkflows = null,
  enableSorting = false,
}) => {
  // Core hooks
  const { employees, totalCount, loading, error } = useEmployeeData(
    selectedMUM,
    roleConfig
  );

  const {
    selectedWorkflow,
    workflowDescription,
    showWorkflow,
    setShowWorkflow,
    isSubmitting,
    showConfirmDialog,
    setShowConfirmDialog,
    showTransition,
    setShowTransition,
    conflictEmployees,
    handleWorkflowChange,
    checkWorkflowConflicts,
    startWorkflow,
  } = useWorkflowManagement();

  const {
    selectedEmployees,
    handleCheckboxChange,
    clearSelection,
    selectionCount,
  } = useEmployeeSelection();

  const {
    selectedEmptyPositions,
    handleEmptyPositionSelect,
    clearEmptyPositions,
    emptyPositionCount,
  } = useEmptyPositionSelection();

  const { notification, showNotification } = useNotifications();

  // Form data state for workflows that require forms
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});
  const [workflowMode, setWorkflowMode] = useState(null);

  // Get workflow icon
  const getWorkflowIcon = (workflowName) => {
    if (!workflowName) return null;
    const workflowLower = workflowName.toLowerCase();

    if (workflowLower.includes("exit")) return exitSvg;
    if (workflowLower.includes("develop")) return developingSvg;
    if (workflowLower.includes("promote")) return promoteSvg;
    if (workflowLower.includes("transfer")) return transferSvg;
    if (
      workflowLower.includes("skill plan") ||
      workflowLower.includes("skillplan")
    )
      return skillPlanSvg;
    if (workflowLower.includes("request position")) return requestSvg;
    if (workflowLower.includes("position training")) return trainingSvg;
    return null;
  };

  // Get workflow CSS class
  const getWorkflowClass = (workflowName) => {
    if (!workflowName) return "";
    const workflowLower = workflowName.toLowerCase();

    if (workflowLower.includes("exit")) return styles.workflowExit;
    if (workflowLower.includes("develop")) return styles.workflowDeveloping;
    if (workflowLower.includes("promote")) return styles.workflowPromote;
    if (workflowLower.includes("transfer")) return styles.workflowTransfer;
    if (
      workflowLower.includes("skill plan") ||
      workflowLower.includes("skillplan")
    )
      return styles.workflowSkillPlan;
    if (workflowLower.includes("request position"))
      return styles.workflowRequestPosition;
    if (workflowLower.includes("position training"))
      return styles.workflowPositionTraining;
    return "";
  };

  // Handle form field changes
  const handleFormFieldChange = (fieldName, value) => {
    setFormData((prev) => ({
      ...prev,
      [fieldName]: value,
    }));

    // Clear error for this field
    setFormErrors((prev) => ({
      ...prev,
      [fieldName]: null,
    }));
  };

  // Validate form before submission
  const validateForm = () => {
    if (!workflowDescription?.formFields) return true;

    const errors = {};
    let isValid = true;

    workflowDescription.formFields.forEach((field) => {
      // Skip validation for workflowNote field as it's always optional
      if (field.name === "workflowNote") {
        return;
      }

      // Skip validation for readonly fields
      if (field.readonly) {
        return;
      }

      // Check conditional requirements
      if (field.condition) {
        const conditionField = formData[field.condition.field];
        if (
          field.condition.notEquals &&
          conditionField === field.condition.notEquals
        ) {
          return; // Skip validation for this field
        }
        if (
          field.condition.equals &&
          conditionField !== field.condition.equals
        ) {
          return; // Skip validation for this field
        }
      }

      // Required field validation
      if (field.required && !formData[field.name]) {
        errors[field.name] = `${field.label} is required`;
        isValid = false;
      }

      // Number validation
      if (field.type === "number" && formData[field.name]) {
        const value = parseFloat(formData[field.name]);
        if (isNaN(value) || value <= 0) {
          errors[field.name] = `Please enter a valid positive number`;
          isValid = false;
        }
      }

      // Date validation (Sunday check)
      if (
        field.type === "date" &&
        field.validation === "sunday" &&
        formData[field.name]
      ) {
        const [year, month, day] = formData[field.name].split("-").map(Number);
        const selectedDate = new Date(Date.UTC(year, month - 1, day));
        if (selectedDate.getUTCDay() !== 0) {
          errors[field.name] = `Please select a Sunday`;
          isValid = false;
        }
      }

      // Max length validation
      if (field.maxLength && formData[field.name]?.length > field.maxLength) {
        errors[field.name] = `Maximum ${field.maxLength} characters allowed`;
        isValid = false;
      }
    });

    setFormErrors(errors);
    return isValid;
  };

  // Handle checkbox selection for employees
  const handleEmployeeSelect = (employeeId, hasWorkflow) => {
    if (hasWorkflow) {
      showNotification(
        "This employee already has an active workflow. Please deselect to continue.",
        "error"
      );
      return;
    }
    handleCheckboxChange(employeeId, hasWorkflow);
  };

  // Start workflow process
  const handleStartWorkflow = () => {
    // Determine which mode we're in based on selections
    if (emptyPositionCount > 0) {
      setWorkflowMode("position");
      // Pre-select Request Position workflow
      handleWorkflowChange("11"); // Request Position workflow ID
    } else if (selectionCount > 0) {
      setWorkflowMode("employee");
    } else {
      showNotification(
        "Please select employees or vacant positions first.",
        "warning"
      );
      return;
    }
    setShowWorkflow(true);
  };

  // Close workflow panel and reset selections
  const handleCloseWorkflow = () => {
    setShowWorkflow(false);
    clearSelection();
    clearEmptyPositions();
    setFormData({});
    setFormErrors({});
    setWorkflowMode(null);
    handleWorkflowChange(""); // Reset selected workflow
  };

  // Start position request workflow
  const startPositionRequestWorkflow = async () => {
    try {
      // Convert selected positions to proper format
      const positions = selectedEmptyPositions.map((posKey) => {
        const [storeCorp, roleType, index] = posKey.split("-");

        let positionType;
        if (roleType === "RESTAURANT_MANAGER") {
          positionType = "RM";
        } else if (roleType === "ASSISTANT_MANAGER") {
          positionType = "AM";
        } else if (roleType === "SHIFT_LEADER") {
          positionType = "SL";
        } else if (roleType === "CREW_MEMBER") {
          positionType = "CM";
        } else {
          positionType = roleType;
        }

        return {
          store_corp: storeCorp,
          position_type: positionType,
          position_index: parseInt(index) || 0,
        };
      });

      const response = await apiClient.post("/api/position-requests", {
        positions: positions,
        workflow_id: selectedWorkflow,
        fields: formData,
        note:
          formData.positionDetails ||
          formData.trainingPlan ||
          formData.workflowNote ||
          null,
      });

      const result = response.data;
      showNotification(
        `${result.positions_created} position request(s) submitted successfully!`,
        "success"
      );
      clearEmptyPositions();
      clearSelection();
      setFormData({});
      setShowWorkflow(false);
      setShowConfirmDialog(false);
      setWorkflowMode(null);
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || "Failed to submit position requests";
      showNotification(`Error: ${errorMessage}`, "error");
      setShowConfirmDialog(false);
    }
  };

  // Initiate workflow with confirmation
  const initiateWorkflow = async () => {
    if (!selectedWorkflow) {
      showNotification("Please select a workflow type first.", "error");
      return;
    }

    // Position Training (workflow 12) requires employee selection
    if (selectedWorkflow === "12") {
      if (selectedEmployees.length === 0) {
        showNotification(
          "Please select an employee for position training.",
          "error"
        );
        return;
      }

      if (selectedEmployees.length > 1) {
        showNotification(
          "Please select only one employee for position training.",
          "error"
        );
        return;
      }

      // Validate form
      if (workflowDescription?.requiresForm && !validateForm()) {
        showNotification(
          "Please fill in all required fields correctly.",
          "error"
        );
        return;
      }

      setShowConfirmDialog(true);
      return;
    }

    // Request Position workflow (workflow 11) for vacant positions
    const isPositionWorkflow = selectedWorkflow === "11";

    if (isPositionWorkflow && emptyPositionCount > 0) {
      // Validate form if required
      if (workflowDescription?.requiresForm && !validateForm()) {
        showNotification(
          "Please fill in all required fields correctly.",
          "error"
        );
        return;
      }
      // For position workflows, go directly to confirmation
      setShowConfirmDialog(true);
    } else {
      // Existing employee workflow logic
      if (selectedEmployees.length === 0) {
        showNotification(
          "Please select at least one employee to start a workflow.",
          "error"
        );
        return;
      }

      // Validate form if required
      if (workflowDescription?.requiresForm && !validateForm()) {
        showNotification(
          "Please fill in all required fields correctly.",
          "error"
        );
        return;
      }

      try {
        const noConflicts = await checkWorkflowConflicts(selectedEmployees);
        if (noConflicts) {
          setShowConfirmDialog(true);
        } else {
          const conflictNames = conflictEmployees
            .map((emp) => emp.employee_name || `ID: ${emp.user_id}`)
            .join(", ");
          showNotification(
            `Cannot start workflow. The following employees already have active workflows: ${conflictNames}`,
            "error"
          );
        }
      } catch (error) {
        showNotification(`Error checking workflows: ${error.message}`, "error");
      }
    }
  };

  // Confirm and start workflow
  const confirmWorkflow = async () => {
    try {
      // Special handling for Position Training (workflow 12)
      if (selectedWorkflow === "12") {
        // Position Training uses employee selection but routes to position tables
        if (selectedEmployees.length === 0) {
          showNotification("Please select an employee for training.", "error");
          return;
        }

        // Add role context from current table
        const roleContext = roleConfig.singularName || roleConfig.displayName;

        await startWorkflow(
          selectedEmployees,
          { ...formData },
          { ...roleConfig, role_context: roleContext },
          selectedMUM
        );

        showNotification(
          "Position training workflow started successfully!",
          "success"
        );
        clearSelection();
        clearEmptyPositions();
        setFormData({});
        setShowWorkflow(false);
        setShowConfirmDialog(false);
        setWorkflowMode(null);
        return;
      }

      // Check for Request Position workflow (11)
      const isPositionWorkflow = selectedWorkflow === "11";

      if (isPositionWorkflow && emptyPositionCount > 0) {
        await startPositionRequestWorkflow();
      } else {
        // Regular employee workflow
        await startWorkflow(
          selectedEmployees,
          formData,
          roleConfig,
          selectedMUM
        );
        showNotification("Workflow started successfully!", "success");
        clearSelection();
        clearEmptyPositions();
        setFormData({});
        setShowWorkflow(false);
        setShowConfirmDialog(false);
        setWorkflowMode(null);
      }
    } catch (error) {
      showNotification(`Error starting workflow: ${error.message}`, "error");
      setShowConfirmDialog(false);
    }
  };

  // Clean up modal state
  useEffect(() => {
    if (showConfirmDialog) {
      document.body.classList.add("has-modal");
    } else {
      document.body.classList.remove("has-modal");
    }
    return () => {
      document.body.classList.remove("has-modal");
    };
  }, [showConfirmDialog]);

  // Get selected employee details
  const getSelectedEmployeeDetails = () => {
    const allEmployees = employees.flatMap((store) => store.employees);
    return selectedEmployees.map((empId) => {
      const employee = allEmployees.find((e) => e.user_id === empId);
      const hasConflict = conflictEmployees.some((c) => c.user_id === empId);
      return { ...employee, hasConflict };
    });
  };

  // Get selected position details for confirmation dialog
  const getSelectedPositionDetails = () => {
    return selectedEmptyPositions.map((posKey) => {
      const [storeCorp, roleType, index] = posKey.split("-");

      // Convert role type to readable format
      let positionType;
      if (roleType === "RESTAURANT_MANAGER") {
        positionType = "Restaurant Manager";
      } else if (roleType === "ASSISTANT_MANAGER") {
        positionType = "Assistant Manager";
      } else if (roleType === "SHIFT_LEADER") {
        positionType = "Shift Leader";
      } else {
        positionType = roleType
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (l) => l.toUpperCase());
      }

      return {
        store_corp: storeCorp,
        position_type: positionType,
        position_index: parseInt(index) || 0,
      };
    });
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        Loading {roleConfig.displayName}...
      </div>
    );
  }

  if (error) {
    return (
      <div className={styles.errorContainer}>Error loading data: {error}</div>
    );
  }

  return (
    <div
      className={`${styles.wrapper} ${showWorkflow ? styles.withWorkflow : ""}`}
    >
      <NotificationBanner notification={notification} />

      {showTransition ? (
        <WorkflowTransition
          workflowName={workflowDescription?.displayName}
          selectedMUM={selectedMUM}
          role={roleConfig.singularName}
          userIds={selectedEmployees.join(",")}
          onComplete={() => setShowTransition(false)}
        />
      ) : (
        <>
          <div className={styles.tableContainer}>
            <div className={styles.header}>
              <h2>
                {roleConfig.displayName}: <span>{totalCount}</span>
                {(selectionCount > 0 || emptyPositionCount > 0) && (
                  <button
                    className={styles.startWorkflowButton}
                    onClick={handleStartWorkflow}
                  >
                    Start Workflow
                    {selectionCount > 0 &&
                      ` (${selectionCount} employee${
                        selectionCount > 1 ? "s" : ""
                      })`}
                    {emptyPositionCount > 0 &&
                      ` (${emptyPositionCount} position${
                        emptyPositionCount > 1 ? "s" : ""
                      })`}
                  </button>
                )}
              </h2>
            </div>

            <EmployeeTableGrid
              employees={employees}
              storeData={storeData}
              roleConfig={roleConfig}
              selectedEmployees={selectedEmployees}
              selectedEmptyPositions={selectedEmptyPositions}
              onEmployeeSelect={handleEmployeeSelect}
              onEmptyPositionSelect={handleEmptyPositionSelect}
              onEmployeeClick={onNavigateToProfile}
              getWorkflowIcon={getWorkflowIcon}
              getWorkflowClass={getWorkflowClass}
              enableSorting={enableSorting}
            />
          </div>

          {showWorkflow && (
            <WorkflowPanel
              selectedWorkflow={selectedWorkflow}
              workflowDescription={workflowDescription}
              onWorkflowChange={handleWorkflowChange}
              formData={formData}
              formErrors={formErrors}
              onFormFieldChange={handleFormFieldChange}
              onStartWorkflow={initiateWorkflow}
              onClosePanel={handleCloseWorkflow}
              isSubmitting={isSubmitting}
              customWorkflows={customWorkflows}
              workflowMode={workflowMode}
              roleConfig={roleConfig}
            />
          )}

          {showConfirmDialog && (
            <ConfirmationDialog
              workflowName={workflowDescription?.displayName}
              selectedEmployees={
                workflowMode === "position"
                  ? getSelectedPositionDetails()
                  : getSelectedEmployeeDetails()
              }
              onConfirm={confirmWorkflow}
              onCancel={() => setShowConfirmDialog(false)}
              isSubmitting={isSubmitting}
              isPositionRequest={workflowMode === "position"}
            />
          )}
        </>
      )}
    </div>
  );
};

export default GenericEmployeeTable;